import React from "react";
import OdooServices from "./OdooServices";
import HeroSection from "../common/HeroSection";
import ServiceSidebar from "../services/ServiceSidebar";
import { BookOpen, ChartNoAxesColumn, CircleCheckBig, Database, Layers, Settings, Users } from "lucide-react";
import { Link } from "react-router-dom";

export default function OdooImplementation() {
  return (
    <div>
      <HeroSection
        title="Odoo Implementation Services"
        breadcrumb={[
          { name: "Services", url: "/services" },
          { name: "Odoo Services", url: "/services/odoo-services" },
          {
            name: "Odoo Implementation",
            url: "/services/odoo-services/odoo-implementation",
          },
        ]}
        background="/assets/images/career.jpg"
      />
      <div className="bg-white">
        <div className="py-16 px-4 mx-auto container">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 px-4">
          {/* Sidebar (OdooServices) */}
          <OdooServices />

          {/* <!-- Content Section --> */}

          <div className="lg:col-span-2 ">
            <div className="">
              <div className="relative rounded-xl overflow-hidden mb-8">
                <img
                  alt="Odoo Implementation Services"
                  loading="lazy"
                  width="700"
                  height="450"
                  decoding="async"
                  data-nimg="1"
                  className="w-full h-auto object-cover rounded-xl"
                  src="/assets/images/odoo/odoo-implementation.png"
                />
              </div>
              <div className="mb-8">
                <span className="text-primary font-medium text-lg inline-block py-1 font-bold rounded-full bg-primary bg-opacity-10 mb-3">
                  Streamlined Implementation
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
                  Odoo <span className="text-primary">Implementation</span>{" "}
                  Services
                </h2>
                <div className="prose prose-lg max-w-none text-gray-600">
                  <p>
                    Successfully implementing Odoo is a critical step in
                    transforming your business operations. At{" "}
                    <span className="text-primary font-medium">
                      TISSA Technology
                    </span>
                    , we provide comprehensive Odoo implementation services to
                    ensure a smooth transition to this powerful business
                    management system.
                  </p>
                  <p>
                    Our experienced team follows a structured methodology that
                    covers every aspect of implementation, from initial needs
                    assessment to post-launch support. We work closely with your
                    team to understand your business workflows, configure Odoo
                    to meet your specific requirements, and ensure your data is
                    migrated accurately.
                  </p>
                  <p>
                    With our implementation expertise, you can avoid common
                    pitfalls, minimize business disruption, and ensure high user
                    adoption rates. Whether you're implementing Odoo for the
                    first time or migrating from another system, our team will
                    guide you through each step of the process for a successful
                    deployment.
                  </p>
                </div>
              </div>
              <div className="mb-12">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">
                  Our Odoo Implementation Services
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white p-6 rounded-xl shadow-soft border border-gray-100 hover:shadow-md transition-shadow duration-300">
                    <div className="bg-primary bg-opacity-10 p-3 rounded-full inline-flex items-center justify-center mb-4">
                      <Settings className="h-6 w-6 text-primary" />
                    </div>
                    <h4 className="text-xl font-semibold text-gray-800 mb-2">
                      Requirements Analysis &amp; Planning
                    </h4>
                    <p className="text-gray-600">
                      Thorough analysis of your business processes and goals to
                      create a detailed implementation plan tailored to your
                      needs.
                    </p>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-soft border border-gray-100 hover:shadow-md transition-shadow duration-300">
                    <div className="bg-primary bg-opacity-10 p-3 rounded-full inline-flex items-center justify-center mb-4">
                      <Layers className="h-6 w-6 text-primary" />
                    </div>
                    <h4 className="text-xl font-semibold text-gray-800 mb-2">
                      System Configuration &amp; Setup
                    </h4>
                    <p className="text-gray-600">
                      Expert setup and configuration of Odoo modules to align
                      with your business workflows and requirements.
                    </p>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-soft border border-gray-100 hover:shadow-md transition-shadow duration-300">
                    <div className="bg-primary bg-opacity-10 p-3 rounded-full inline-flex items-center justify-center mb-4">
                      <Database className="h-6 w-6 text-primary" />
                    </div>
                    <h4 className="text-xl font-semibold text-gray-800 mb-2">
                      Data Migration &amp; Integration
                    </h4>
                    <p className="text-gray-600">
                      Secure and accurate migration of your existing data to
                      Odoo, with integration to other business systems as
                      needed.
                    </p>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-soft border border-gray-100 hover:shadow-md transition-shadow duration-300">
                    <div className="bg-primary bg-opacity-10 p-3 rounded-full inline-flex items-center justify-center mb-4">
                      <Users className="h-6 w-6 text-primary" />
                    </div>
                    <h4 className="text-xl font-semibold text-gray-800 mb-2">
                      User Training &amp; Support
                    </h4>
                    <p className="text-gray-600">
                      Comprehensive training sessions for your team to ensure
                      they can effectively use Odoo for their daily tasks.
                    </p>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-soft border border-gray-100 hover:shadow-md transition-shadow duration-300">
                    <div className="bg-primary bg-opacity-10 p-3 rounded-full inline-flex items-center justify-center mb-4">
                      <BookOpen className="h-6 w-6 text-primary" />
                    </div>
                    <h4 className="text-xl font-semibold text-gray-800 mb-2">
                      Documentation &amp; Knowledge Transfer
                    </h4>
                    <p className="text-gray-600">
                      Detailed documentation of your Odoo implementation for
                      future reference and ongoing knowledge transfer.
                    </p>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-soft border border-gray-100 hover:shadow-md transition-shadow duration-300">
                    <div className="bg-primary bg-opacity-10 p-3 rounded-full inline-flex items-center justify-center mb-4">
                      <ChartNoAxesColumn className="h-6 w-6 text-primary" />
                    </div>
                    <h4 className="text-xl font-semibold text-gray-800 mb-2">
                      Go-Live Support &amp; Optimization
                    </h4>
                    <p className="text-gray-600">
                      Dedicated support during system launch and ongoing
                      optimization to enhance system performance.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mb-12">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">
                  Our Implementation Methodology
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-6 rounded-xl shadow-soft border border-gray-100">
                    <h4 className="text-xl font-bold text-primary mb-4">
                      <span className="inline-block w-8 h-8 bg-primary text-black rounded-full text-center mr-3">
                        1.
                      </span>
                      Discovery Phase
                    </h4>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Analyze business processes and requirements
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Identify key stakeholders and their needs
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Review existing systems and data structures
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Define project scope and objectives
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Develop implementation strategy and timeline
                        </span>
                      </li>
                    </ul>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-soft border border-gray-100">
                    <h4 className="text-xl font-bold text-primary mb-4">
                      <span className="inline-block w-8 h-8 bg-primary text-black rounded-full text-center mr-3">
                        2.
                      </span>
                      Planning Phase
                    </h4>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Create detailed implementation plan
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Define system architecture and configurations
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Establish data migration strategy
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Design custom development specifications
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Define testing procedures and acceptance criteria
                        </span>
                      </li>
                    </ul>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-soft border border-gray-100">
                    <h4 className="text-xl font-bold text-primary mb-4">
                      <span className="inline-block w-8 h-8 bg-primary text-black rounded-full text-center mr-3">
                        3.
                      </span>
                      Configuration Phase
                    </h4>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Set up Odoo environment (cloud or on-premise)
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Configure core modules based on requirements
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Implement company structure and user permissions
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Customize workflows and business rules
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Set up reporting and dashboard requirements
                        </span>
                      </li>
                    </ul>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-soft border border-gray-100">
                    <h4 className="text-xl font-bold text-primary mb-4">
                      <span className="inline-block w-8 h-8 bg-primary text-black rounded-full text-center mr-3">
                        4.
                      </span>
                      Development Phase
                    </h4>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Develop custom modules if required
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Customize forms, reports, and user interfaces
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Create data import templates
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Develop integration points with other systems
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Implement automation and advanced features
                        </span>
                      </li>
                    </ul>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-soft border border-gray-100">
                    <h4 className="text-xl font-bold text-primary mb-4">
                      <span className="inline-block w-8 h-8 bg-primary text-black rounded-full text-center mr-3">
                        5.
                      </span>
                      Data Migration Phase
                    </h4>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Extract data from legacy systems
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Transform data to fit Odoo structure
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Validate data quality and integrity
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Conduct test migrations
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Finalize data migration procedures
                        </span>
                      </li>
                    </ul>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-soft border border-gray-100">
                    <h4 className="text-xl font-bold text-primary mb-4">
                      <span className="inline-block w-8 h-8 bg-primary text-black rounded-full text-center mr-3">
                        6.
                      </span>
                      Testing Phase
                    </h4>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Conduct unit testing of configured modules
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Perform integration testing across modules
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Validate business process workflows
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Execute user acceptance testing
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Address and resolve identified issues
                        </span>
                      </li>
                    </ul>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-soft border border-gray-100">
                    <h4 className="text-xl font-bold text-primary mb-4">
                      <span className="inline-block w-8 h-8 bg-primary text-black rounded-full text-center mr-3">
                        7.
                      </span>
                      Training Phase
                    </h4>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Develop training materials and documentation
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Conduct role-based training sessions
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Provide hands-on practice opportunities
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Train system administrators and power users
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Address user questions and concerns
                        </span>
                      </li>
                    </ul>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-soft border border-gray-100">
                    <h4 className="text-xl font-bold text-primary mb-4">
                      <span className="inline-block w-8 h-8 bg-primary text-black rounded-full text-center mr-3">
                        8.
                      </span>
                      Go-Live Phase
                    </h4>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Finalize system configurations
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Perform final data migration
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Conduct final system verification
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Provide intensive go-live support
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Monitor system performance and stability
                        </span>
                      </li>
                    </ul>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-soft border border-gray-100">
                    <h4 className="text-xl font-bold text-primary mb-4">
                      <span className="inline-block w-8 h-8 bg-primary text-black rounded-full text-center mr-3">
                        9.
                      </span>
                      Post-Implementation Support
                    </h4>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Provide ongoing technical support
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Address post-implementation issues
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Deliver additional training as needed
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Optimize system based on user feedback
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">
                          Plan for future enhancements and upgrades
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="mb-12">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">
                  Benefits of Professional Odoo Implementation
                </h3>
                <div className="bg-gray-50 p-6 rounded-xl border-l-4 border-primary">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-start">
                      <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">
                        Faster implementation timeframe compared to in-house
                        efforts
                      </span>
                    </div>
                    <div className="flex items-start">
                      <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">
                        Reduced risk of implementation failure or delays
                      </span>
                    </div>
                    <div className="flex items-start">
                      <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">
                        Expert guidance to avoid common pitfalls and challenges
                      </span>
                    </div>
                    <div className="flex items-start">
                      <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">
                        Optimized system setup tailored to your specific
                        industry
                      </span>
                    </div>
                    <div className="flex items-start">
                      <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">
                        Higher user adoption rates with professional training
                      </span>
                    </div>
                    <div className="flex items-start">
                      <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">
                        Minimal business disruption during transition
                      </span>
                    </div>
                    <div className="flex items-start">
                      <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">
                        Long-term support from experienced Odoo professionals
                      </span>
                    </div>
                    <div className="flex items-start">
                      <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">
                        Knowledge transfer to enable self-sufficiency
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mb-12">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">
                  Success Story
                </h3>
                <div className="bg-white p-6 rounded-xl shadow-soft border border-gray-100">
                  <h4 className="text-xl font-bold text-primary mb-4">
                    Manufacturing Company Transformation
                  </h4>
                  <p className="text-gray-600 mb-4">
                    A mid-sized manufacturing company was struggling with
                    disconnected systems for inventory, production, and sales.
                    They were using multiple software solutions that didn't
                    communicate with each other, leading to data inconsistencies
                    and inefficient processes.
                  </p>
                  <p className="text-gray-600 mb-4">
                    Our team implemented a comprehensive Odoo solution that
                    integrated manufacturing, inventory, sales, purchasing, and
                    accounting into a single platform. We configured the system
                    to match their specific manufacturing workflows, migrated
                    data from their legacy systems, and provided thorough
                    training for all users.
                  </p>
                  <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-primary font-bold text-2xl">30%</div>
                      <div className="text-gray-600">
                        Reduction in Production Lead Time
                      </div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-primary font-bold text-2xl">25%</div>
                      <div className="text-gray-600">
                        Decrease in Operating Costs
                      </div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-primary font-bold text-2xl">95%</div>
                      <div className="text-gray-600">
                        Data Accuracy Improvement
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-8 rounded-xl border border-blue-100">
                  <h4 className="text-2xl font-bold text-gray-800 mb-3">
                    Ready to Implement Odoo for Your Business?
                  </h4>
                  <p className="text-gray-600 mb-6">
                    Contact our Odoo implementation experts today to discuss how
                    we can help transform your business operations with a
                    tailored Odoo solution.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Link
                      className="inline-block text-blue-700 font-bold bg-primary hover:bg-primary-dark font-medium py-3 px-6 rounded-lg transition-colors duration-300"
                      to="/contact-us/"
                    >
                      Request a Free Consultation
                    </Link>
                    <Link
                      className="inline-block border border-primary text-primary hover:bg-blue-700 hover:text-white font-medium py-3 px-6 rounded-lg transition-colors duration-300"
                      to="/services/odoo-services/"
                    >
                      Explore All Odoo Services
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
      
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-xl shadow-soft">
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  How long does a typical Odoo implementation take?
                </h3>
                <p className="text-gray-600">
                  The timeline for Odoo implementation varies based on your
                  business complexity and requirements. A basic implementation
                  for a small business can take 4-8 weeks, while more complex
                  implementations for larger organizations may take 3-6 months.
                  During our initial consultation, we'll provide a customized
                  timeline for your specific implementation needs.
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-soft">
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  Can we continue business operations during implementation?
                </h3>
                <p className="text-gray-600">
                  Yes, our implementation approach is designed to minimize
                  disruption to your daily operations. We typically work in
                  parallel with your existing systems, and the final cutover to
                  Odoo is carefully planned to occur during off-hours or lower
                  business activity periods. Our team works closely with yours
                  to ensure a smooth transition with minimal impact on your
                  business continuity.
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-soft">
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  How do you handle data migration from our existing systems?
                </h3>
                <p className="text-gray-600">
                  We follow a structured data migration approach that includes
                  data extraction from your existing systems, data cleansing and
                  transformation to fit Odoo's structure, and validation to
                  ensure integrity. We perform test migrations before the final
                  go-live to identify and resolve any issues. Our goal is to
                  ensure all your critical historical data is accurately
                  transferred to Odoo.
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-soft">
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  What kind of training do you provide as part of
                  implementation?
                </h3>
                <p className="text-gray-600">
                  Our training program is comprehensive and tailored to
                  different user roles within your organization. We provide
                  administrator training, end-user training, and specialized
                  training for specific departments (sales, accounting,
                  inventory, etc.). Training includes both classroom-style
                  sessions and hands-on workshops. We also provide custom
                  documentation and quick reference guides for your team's
                  ongoing use.
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-soft">
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  How do you ensure our specific business requirements are met?
                </h3>
                <p className="text-gray-600">
                  We begin every implementation with a thorough requirements
                  analysis phase where we document your specific business
                  processes and needs. Throughout the implementation, we follow
                  an iterative approach with regular checkpoints to ensure the
                  system is being configured to meet these requirements. We also
                  conduct user acceptance testing with your team to verify that
                  the implemented solution satisfies your business needs before
                  going live.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
